export function buildHomeworkTable(entries = []) {
  const dataMap = new Map([[1, new Map()]]);
  let maxCol = 0;
  let maxRow = 1;

  entries.forEach((entry) => {
    const col = Number.parseInt(entry.col, 10);
    const row = Number.parseInt(entry.row, 10);

    if (Number.isNaN(col) || Number.isNaN(row)) {
      return;
    }

    maxCol = Math.max(maxCol, col);
    maxRow = Math.max(maxRow, row);

    if (!dataMap.has(row)) {
      dataMap.set(row, new Map());
    }

    dataMap.get(row).set(col, entry.data);
    // Only homework cells own a deadline. Subject cells can be appended by
    // the API after the assignment record, and must not overwrite homework
    // in the next column with their empty deadline.
    if (col > 1) {
      dataMap.get(row).set(col + 1, entry.deadline);
    }
  });

  maxCol = Math.max(maxCol + 1, 3);
  dataMap.get(1).set(3, 'Deadline');

  return {
    tableData: dataMap,
    maxCol,
    maxRow,
  };
}

export function buildDeadlineTable(entries = []) {
  const dataMap = new Map([
    [1, new Map([[1, 'Subject']])],
    [2, new Map([[1, 'Homework']])],
  ]);

  let maxCol = 1;

  entries.forEach((entry) => {
    maxCol += 1;
    dataMap.get(1).set(maxCol, entry.subject);
    dataMap.get(2).set(maxCol, entry.data);
  });

  return {
    tableData: dataMap,
    maxCol,
    maxRow: 2,
  };
}

function getSelectedTagName(subject, subjectSelections) {
  const normalizedSubject = String(subject || '').trim().toLocaleLowerCase();
  const selection = Object.entries(subjectSelections || {}).find(([selectionSubject]) =>
    selectionSubject.trim().toLocaleLowerCase() === normalizedSubject)?.[1];

  return String(selection?.tag_name || '').trim();
}

function getAssignmentTagName(assignment, subject) {
  if (assignment.class_tag_name) return String(assignment.class_tag_name).trim();

  const combinedSubject = String(assignment.subject || '');
  const prefix = `${subject} · `;
  return combinedSubject.startsWith(prefix) ? combinedSubject.slice(prefix.length).trim() : '';
}

export function buildClassHomeworkTable(assignments = [], subjects = [], subjectSelections = {}) {
  const dataMap = new Map([
    [1, new Map([[1, 'Subject'], [2, 'Homework'], [3, 'Deadline']])],
  ]);
  const subjectTags = new Map();

  const rows = [];
  const includedAssignments = new Set();
  const boardRows = subjects.map((item) => ({
    rowId: typeof item === 'object' && item?.row_id !== undefined
      ? String(item.row_id)
      : null,
    subject: String(typeof item === 'string' ? item : item?.subject || '').trim(),
    tagName: String(
      (typeof item === 'object' && (item?.class_tag_name || item?.tag_name))
      || getSelectedTagName(typeof item === 'string' ? item : item?.subject, subjectSelections),
    ).trim(),
  })).filter((item) => item.subject);
  const canMatchPhysicalRows = boardRows.some((item) => item.rowId !== null)
    && assignments.some((assignment) => assignment.row_id !== undefined && assignment.row_id !== null);
  const remainingSubjectRows = new Map();
  boardRows.forEach(({ subject }) => {
    remainingSubjectRows.set(subject, (remainingSubjectRows.get(subject) || 0) + 1);
  });

  boardRows.forEach(({ rowId, subject, tagName }) => {
    let matchingIndexes = [];
    if (canMatchPhysicalRows && rowId !== null) {
      matchingIndexes = assignments.reduce((matches, assignment, index) => {
        if (String(assignment.row_id) === rowId) matches.push(index);
        return matches;
      }, []);
    } else {
      const availableMatches = assignments.reduce((matches, assignment, index) => {
        const assignmentSubject = assignment.class_subject || assignment.subject;
        if (!includedAssignments.has(index) && assignmentSubject === subject) matches.push(index);
        return matches;
      }, []);
      matchingIndexes = remainingSubjectRows.get(subject) === 1
        ? availableMatches
        : availableMatches.slice(0, 1);
    }

    matchingIndexes.forEach((index) => {
      if (includedAssignments.has(index)) return;
      rows.push(assignments[index]);
      includedAssignments.add(index);
    });
    if (!matchingIndexes.length) {
      rows.push({
        subject,
        class_subject: subject,
        class_tag_name: tagName,
        data: '',
        deadline: '',
      });
    }
    remainingSubjectRows.set(subject, remainingSubjectRows.get(subject) - 1);
  });
  assignments.forEach((assignment, index) => {
    if (!includedAssignments.has(index)) rows.push(assignment);
  });

  rows.forEach((assignment, index) => {
    const row = index + 2;
    const subject = assignment.class_subject || assignment.subject || 'General';
    const tagName = String(
      getAssignmentTagName(assignment, subject) || getSelectedTagName(subject, subjectSelections),
    ).trim();
    dataMap.set(row, new Map([
      [1, subject],
      [2, assignment.data || ''],
      [3, assignment.deadline ? String(assignment.deadline).slice(0, 10) : ''],
    ]));
    if (tagName) subjectTags.set(row, tagName);
  });

  return {
    tableData: dataMap,
    subjectTags,
    maxCol: 3,
    maxRow: Math.max(rows.length + 1, 1),
  };
}
